import { Flex, useDisclosure } from "@chakra-ui/react";

import { CountryComponentWrapper } from "components/Common/CountryComponentWrapper";
import { CashbackModal } from "components/Dashboard/Cashback/CashbackModal";
import { OFText } from "design_system/components/OFText";
import { InfoEllipseIcon } from "design_system/icons";
import { RegionType } from "graphql/generated";
import { colors } from "src/colors";
import { useConfig } from "src/hooks";
import { CheckoutTypes } from "src/types/checkout";
import { addTransSuffixCountry } from "src/utils";
import getNameProp from "src/utils/getNameProp";
import { formatPrice } from "src/utils/priceFormat";

import { SummaryItem } from "./SummaryItem";
import { LABEL_TEXT_STYLE, VALUE_TEXT_STYLE } from "./constant/style";

type Props = CheckoutTypes & {
  t: (key: string) => string;
  isRenewal?: boolean;
  hideShipping?: boolean;
  hasUnpaidTextConsult?: boolean;
  shouldShowCashback?: boolean;
};

export const infoIconProps = {
  cursor: "pointer",
  size: 20,
  color: colors.grey[900]
};

const SummarySection = ({
  t,
  summary,
  isRenewal,
  hideShipping,
  hasUnpaidTextConsult,
  shouldShowCashback = true
}: Props) => {
  const { subTotalNow, shippingFee, cashbackRedeemed } = summary;
  const transCheckoutKey = addTransSuffixCountry("checkout");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { addSuffixCountry } = useConfig();

  const consultFee = summary?.teleConsultFee;
  const hasConsultFee = consultFee > 0;

  return (
    <Flex flexDirection="column">
      {(hasConsultFee || isRenewal) && (
        <SummaryItem key="tele-consult" {...getNameProp("order-item-summary-section")}>
          <Flex alignItems="center">
            <OFText textStyle={LABEL_TEXT_STYLE}>
              {t(`${addSuffixCountry("checkout")}:consult-fee-label`)}
            </OFText>
          </Flex>
          <Flex alignItems="center">
            <OFText
              textStyle={VALUE_TEXT_STYLE}
              color={hasUnpaidTextConsult ? null : colors.grey[700]}
            >
              {formatPrice(consultFee || 0)}
            </OFText>
          </Flex>
        </SummaryItem>
      )}

      {!hideShipping && (
        <SummaryItem
          label={t("checkout:shipping-label")}
          value={
            shippingFee === 0 ? t("checkout:free-shipping-label") : `${formatPrice(shippingFee)}`
          }
          {...getNameProp("shipping-fee")}
        />
      )}

      <CountryComponentWrapper countries={[RegionType.Sg]}>
        <SummaryItem
          label={t("checkout:subtotal-label")}
          value={formatPrice(subTotalNow)}
          {...getNameProp("subtotal")}
        />
      </CountryComponentWrapper>

      {shouldShowCashback && (
        <SummaryItem {...getNameProp("cashback-redeemed")}>
          <Flex alignItems="center">
            <OFText textStyle={LABEL_TEXT_STYLE}>
              {t(`${transCheckoutKey}:cashback-redeemed`)}
            </OFText>

            <InfoEllipseIcon {...infoIconProps} onClick={onOpen} />
          </Flex>
          <OFText textStyle={VALUE_TEXT_STYLE}>{formatPrice(cashbackRedeemed)}</OFText>
        </SummaryItem>
      )}
      <CashbackModal isOpen={isOpen} onClose={onClose} />
    </Flex>
  );
};

export default SummarySection;
